import { useMemo } from "react";
import { useStore } from "../index";
import { EMPTY_JWT_PAYLOAD, parseJwt } from "../slices/auth";
import type { JwtPayload } from "../types";

// Selector for parsed JWT token object
export const useApiTokenObject = (): JwtPayload => {
  const apiToken = useStore((state) => state.apiToken);
  return useMemo(
    () => (apiToken ? parseJwt(apiToken) : EMPTY_JWT_PAYLOAD),
    [apiToken],
  );
};

// Selector for user ID from token
export const useMyUserId = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).user_id;
  });
};

// Selector for device ID from token
export const useMyDeviceId = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).device_id;
  });
};

// Selector for username from token
export const useMyUsername = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).username;
  });
};

// Selector for discriminator from token
export const useMyDiscriminator = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).discriminator;
  });
};

// Selector for full handle (server-provided), with username/user_id fallback
export const useMyHandle = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    const token = parseJwt(state.apiToken);
    return token.full_handle || token.username || token.user_id;
  });
};

// Selector for app ID from token
export const useAppId = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).app_id;
  });
};

// Selector for primary device ID
export const useMyPrimaryDeviceId = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    const token = parseJwt(state.apiToken);
    return token.primary_device_id || token.device_id;
  });
};

// Selector for checking if this is the primary device
export const useIsPrimaryDevice = (): boolean => {
  return useStore((state) => {
    if (!state.apiToken) return false;
    return parseJwt(state.apiToken).is_primary_device || false;
  });
};

// Selector for checking if registered (has device ID)
export const useIsRegistered = (): boolean => {
  return useStore((state) => {
    if (!state.apiToken) return false;
    return !!parseJwt(state.apiToken).device_id;
  });
};

// Selector for phone number with prefix
export const useMyNumberPrefixed = (): string | undefined => {
  return useStore((state) => {
    return state.myNumber ? `+${state.myNumber}` : undefined;
  });
};

// Selector for checking if device is linked
export const useHasLinkedDevice = (): boolean => {
  return useStore((state) => {
    if (!state.apiToken) return false;
    return !!parseJwt(state.apiToken).linked_device1;
  });
};

// Selector for linked device ID
export const useLinkedDeviceDeviceId = (): string | undefined => {
  return useStore((state) => {
    if (!state.apiToken) return undefined;
    return parseJwt(state.apiToken).linked_device1?.device_id;
  });
};

// Selector for root title (derived from active tab)
export const useRootTitle = (): string => {
  return useStore((state) => {
    const tabPath = state.activeTab.split("/")[1];
    const title = tabPath.split("-")[0];
    return title.charAt(0).toUpperCase() + title.slice(1);
  });
};

// Non-hook getters for use outside React components
export const getApiTokenObject = (): JwtPayload => {
  const token = useStore.getState().apiToken;
  if (!token) return EMPTY_JWT_PAYLOAD;
  return parseJwt(token);
};

export const getMyUserId = (): string | undefined => {
  const token = useStore.getState().apiToken;
  if (!token) return undefined;
  return parseJwt(token).user_id;
};

export const getMyDeviceId = (): string | undefined => {
  const token = useStore.getState().apiToken;
  if (!token) return undefined;
  return parseJwt(token).device_id;
};

export const getMyHandle = (): string | undefined => {
  const token = useStore.getState().apiToken;
  if (!token) return undefined;
  const parsed = parseJwt(token);
  return parsed.full_handle || parsed.username || parsed.user_id;
};
